import React, { ReactElement, useEffect, useState } from 'react';
import classNames from 'classnames';

import Icon from 'atomic/icon/icon';
import type { ReviewListFilter } from 'modules/review/review.interface';
import { REVIEW_LIST_PAGE_LIMIT } from '../../../../constants';
import { ApiPaginationInterface } from 'interfaces/api.interface';
import DynamicLink from 'components/dynamic-link/dynamic-link';
import { ReviewQueryParams } from 'modules/review/review-query-params.enum';

import './pagination.scss';

interface ReviewListPaginationProps {
    mode: 'mobile' | 'desktop';
    handleChangePage: (value: ReviewListFilter) => void;
    totalCount: number;
    pagination?: ApiPaginationInterface;
    currentOffset?: number;
    type?: 'company' | 'product';
}

const ReviewListPagination: React.FC<ReviewListPaginationProps> = ({
    mode,
    pagination,
    currentOffset,
    totalCount,
    type = 'company',
    handleChangePage
}): ReactElement | null => {
    const pages = Math.ceil(totalCount / REVIEW_LIST_PAGE_LIMIT);

    if (pages === 1) {
        return null;
    }

    const initialPage = typeof currentOffset === 'undefined'
        ? (pagination ? Math.ceil(pagination.offset / pagination.limit) + 1 : 1)
        : Math.ceil(currentOffset / REVIEW_LIST_PAGE_LIMIT) + 1;

    const [currentPage, setCurrentPage] = useState(initialPage);

    useEffect(
        () => setCurrentPage(
            typeof currentOffset === 'undefined'
                ? (pagination ? Math.ceil(pagination.offset / pagination.limit) + 1 : 1)
                : Math.ceil(currentOffset / REVIEW_LIST_PAGE_LIMIT) + 1
        ),
        [currentOffset, pagination]
    );

    const handleClick = (page: number): void => {
        const offset = (page - 1) * REVIEW_LIST_PAGE_LIMIT;
        const limit = REVIEW_LIST_PAGE_LIMIT;

        setCurrentPage(page);
        handleChangePage({ offset, limit });
    };

    const isDesktop = mode === 'desktop';

    const items = [];
    let dotsBeforeAdded = false;
    let dotsAfterAdded = false;

    for (let i = 1; i <= pages; i++) {
        if (i > 1 && i < (isDesktop ? currentPage - 1 : currentPage)) {
            if (!dotsBeforeAdded) {
                items.push(
                    <li key={i} className="review-list__pagination-separator">
                        …
                    </li>
                );
                dotsBeforeAdded = true;
            }
        } else if (i > (isDesktop ? currentPage + 1 : currentPage) && i < pages) {
            if (!dotsAfterAdded) {
                items.push(
                    <li key={i} className="review-list__pagination-separator">
                        …
                    </li>
                );
                dotsAfterAdded = true;
            }
        } else {
            items.push(
                <li key={i} className="review-list__pagination-item">
                    {currentPage === i
                        ? (
                            <span className="review-list__pagination-button review-list__pagination-button--active">
                                {i}
                            </span>
                        )
                        : (
                            <DynamicLink
                                locationParams={{
                                    query: {
                                        [ReviewQueryParams.pagination]: i === 1 ? null : i,
                                        [ReviewQueryParams.type]: type === 'product' ? 'produktowe' : 'firmowe'
                                    }
                                }}
                                wrapper={true}
                                onClick={(): void => handleClick(i)}
                                className={classNames('review-list__pagination-button', {
                                    'review-list__pagination-button--active': currentPage === i
                                })}
                            >
                                {i}
                            </DynamicLink>
                        )}
                </li>
            );
        }
    }

    return (
        <ul className="review-list__pagination">
            {currentPage > 1 && (
                <li className="review-list__pagination-item review-list__pagination-item--prev">
                    <DynamicLink
                        locationParams={{
                            query: {
                                [ReviewQueryParams.pagination]: currentPage === 2 ? null : currentPage - 1,
                                [ReviewQueryParams.type]: type === 'product' ? 'produktowe' : 'firmowe'
                            }
                        }}
                        className="review-list__pagination-button"
                        wrapper={true}
                        onClick={(): void => handleClick(currentPage - 1)}
                    >
                        {isDesktop && <Icon icon="arrow-left" className="review-list__pagination-icon" />}
                        Poprzednia
                    </DynamicLink>
                </li>
            )}
            {items}
            {currentPage < pages && (
                <li className="review-list__pagination-item review-list__pagination-item--next">
                    <DynamicLink
                        locationParams={{
                            query: {
                                [ReviewQueryParams.pagination]: currentPage + 1,
                                [ReviewQueryParams.type]: type === 'product' ? 'produktowe' : 'firmowe'
                            }
                        }}
                        className="review-list__pagination-button"
                        wrapper={true}
                        onClick={(): void => handleClick(currentPage + 1)}
                    >
                        Następna
                        {isDesktop && <Icon icon="arrow-right" className="review-list__pagination-icon" />}
                    </DynamicLink>
                </li>
            )}
        </ul>
    );
};

export default ReviewListPagination;
